import { type ReactNode } from 'react'

/**
 * Wraps an explore-page mockup in a browser-window chrome frame.
 *
 * Two jobs:
 *   1. Visual cue — the chrome (traffic lights + URL bar) signals
 *      "this is a screenshot of the product, not a live affordance".
 *      Visitors don't try to click on demo data.
 *   2. Disables pointer events on children so accidental clicks don't
 *      do nothing-mysteriously. `pointer-events: none` cascades from
 *      the chrome wrapper down to whatever real-app component we drop
 *      inside.
 *
 * `address` shows the route this mockup pretends to be ("/app/squadron"
 * etc) — adds context without needing a caption.
 */
export function MockupFrame({
  address,
  children,
  className,
  tone = 'dark',
}: {
  address?: string
  children: ReactNode
  className?: string
  tone?: 'dark' | 'glass'
}) {
  const surfaceClass =
    tone === 'glass'
      ? 'bg-bg/40 backdrop-blur'
      : 'bg-surface'
  return (
    <div
      className={`overflow-hidden rounded-2xl border border-border shadow-2xl shadow-black/40 ${surfaceClass} ${className ?? ''}`}
    >
      {/* Chrome bar — traffic-light dots, then a fake URL bar showing
          the route. Sits flush at the top of the frame so children
          read as "page content". */}
      <div
        className="flex items-center gap-3 border-b border-border bg-bg/60 px-4 py-2.5"
        aria-hidden
      >
        <div className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-full bg-fg-subtle/40" />
          <span className="size-2.5 rounded-full bg-fg-subtle/40" />
          <span className="size-2.5 rounded-full bg-fg-subtle/40" />
        </div>
        {address && (
          <div className="font-display flex-1 truncate rounded-md border border-border/60 bg-bg/40 px-3 py-1 text-center text-xs tracking-wider text-fg-subtle">
            {address}
          </div>
        )}
      </div>
      {/* Pointer-events disabled so the embedded UI never responds to
          clicks. Visitors are looking, not driving. */}
      <div className="pointer-events-none select-none">{children}</div>
    </div>
  )
}
